import type {
  GetAgencyTimesheetValueByIdParams,
  GetAgencyTimesheetValueByIdResponse,
  CreateAgencyTimesheetValueByIdParams,
  CreateAgencyTimesheetValueByIdResponse,
} from '@nxsys/types/api';
import {
  CheckSubDepartmentAssignParams,
  CheckSubDepartmentAssignResponse,
  ConnectNestPensionParams,
  ConnectNestPensionResponse,
  ConnectSmartPensionParams,
  ConnectSmartPensionResponse,
  ConnectZohoResponse,
  CreateBannerParams,
  CreateBannerResponse,
  CreateClientApplicationParams,
  CreateClientApplicationResponse,
  CreateCompanyDraftParams,
  CreateCompanyDraftResponse,
  GetCompanyDraftPensionStatusResponse,
  SendCompanyDraftLinkParams,
  SendCompanyDraftLinkResponse,
  CreateCompanyParams,
  CreateCompanyResponse,
  CreateExpenseCompanyParams,
  CreateExpenseCompanyResponse,
  CreatePrivateIrelandPensionParams,
  CreatePrivateIrelandPensionResponse,
  CreateWorkingPatternParams,
  CreateWorkingPatternResponse,
  DeleteBannerResponse,
  DeleteExpenseCompanyResponse,
  DeletePrivateIrelandPensionResponse,
  DeleteWorkingPatternResponse,
  DisconnectNestPensionResponse,
  DisconnectSmartPensionResponse,
  GetClientApplicationParams,
  GetClientApplicationResponse,
  GetCompanyByIdResponse,
  GetEmailTemplateByIdResponse,
  GetNestSettingResponse,
  GetPensionProvidersResponse,
  GetPrivateIrelandPensionResponse,
  GetZohoSettingsResponse,
  SendTestEmailParams,
  SendTestEmailResponse,
  UpdateBannerParams,
  UpdateBannerResponse,
  UpdateBoostworksSettingsParams,
  UpdateBoostworksSettingsResponse,
  UpdateClientApplicationParams,
  UpdateClientApplicationResponse,
  UpdateCompanyByIdParams,
  UpdateCompanyByIdResponse,
  UpdateEmailTemplateParams,
  UpdateEmailTemplateResponse,
  UpdateExpenseCompanyParams,
  UpdateExpenseCompanyResponse,
  UpdateNestSettingParams,
  UpdateNestSettingResponse,
  UpdatePrivateIrelandPensionParams,
  UpdatePrivateIrelandPensionResponse,
  UpdateSafeRecSettingsParams,
  UpdateSafeRecSettingsResponse,
  UpdateVeriPayeSettingsParams,
  UpdateVeriPayeSettingsResponse,
  UpdateWorkingPatternParams,
  UpdateWorkingPatternResponse,
  UpdateZohoSettingsParams,
  UpdateZohoSettingsResponse,
  ConnectSmartPensionDraftResponse,
} from '@nxsys/types/api/company';
import { IPrivateIrelandPension } from '@nxsys/types/models/pension';
import axios from '../axios';
import { endPoints } from '../endpoints';

export const getCompanyByIdApi = async (
  id: string,
): Promise<GetCompanyByIdResponse> => {
  const response = await axios.get(`${endPoints.company.getDetail}/${id}`);
  return response.data;
};

export const updateCompanyByIdApi = async (
  id: string,
  params: UpdateCompanyByIdParams,
): Promise<UpdateCompanyByIdResponse> => {
  const response = await axios.put(`${endPoints.company.update}/${id}`, params);
  return response.data;
};

export const createExpenseCompanyApi = async (
  companyId: string,
  params: CreateExpenseCompanyParams,
): Promise<CreateExpenseCompanyResponse> => {
  const response = await axios.post(
    endPoints.company.expense(companyId),
    params,
  );
  return response.data;
};

export const updateExpenseCompanyApi = async (
  companyId: string,
  expenseId: string,
  params: UpdateExpenseCompanyParams,
): Promise<UpdateExpenseCompanyResponse> => {
  const response = await axios.put(
    `${endPoints.company.expense(companyId)}/${expenseId}`,
    params,
  );
  return response.data;
};

export const deleteExpenseCompanyApi = async (
  companyId: string,
  expenseId: string,
): Promise<DeleteExpenseCompanyResponse> => {
  const response = await axios.delete(
    `${endPoints.company.expense(companyId)}/${expenseId}`,
  );
  return response.data;
};

export const getPensionProvidersApi = async (
  companyId: string,
): Promise<GetPensionProvidersResponse> => {
  const response = await axios.get(
    `${endPoints.appConfigs}/pension-providers`,
    {
      params: {
        companyId,
      },
    },
  );
  return response.data;
};

export const connectSmartPensionApi = async (
  companyId: string,
  params: ConnectSmartPensionParams,
): Promise<ConnectSmartPensionResponse> => {
  const response = await axios.get(
    `${endPoints.company.connectSmartPension(companyId)}`,
    {
      params,
    },
  );
  return response.data;
};

export const disconnectSmartPensionApi = async (
  companyId: string,
): Promise<DisconnectSmartPensionResponse> => {
  const response = await axios.delete(
    `${endPoints.company.disconnectSmartPension(companyId)}`,
  );
  return response.data;
};

export const connectNestPensionApi = async (
  companyId: string,
  params: ConnectNestPensionParams,
): Promise<ConnectNestPensionResponse> => {
  const response = await axios.post(
    `${endPoints.company.connectNestPension(companyId)}`,
    params,
  );
  return response.data;
};

export const disconnectNestPensionApi = async (
  companyId: string,
): Promise<DisconnectNestPensionResponse> => {
  const response = await axios.post(
    `${endPoints.company.disconnectNestPension(companyId)}`,
  );
  return response.data;
};

export const getNestSettingApi = async (
  companyId: string,
): Promise<GetNestSettingResponse> => {
  const response = await axios.get(
    `${endPoints.company.nestSettings(companyId)}`,
  );
  return response.data;
};

export const updateNestSettingApi = async (
  companyId: string,
  params: UpdateNestSettingParams,
): Promise<UpdateNestSettingResponse> => {
  const response = await axios.put(
    `${endPoints.company.nestSettings(companyId)}`,
    params,
  );
  return response.data;
};

export const getZohoSettingsApi = async (
  companyId: string,
): Promise<GetZohoSettingsResponse> => {
  const response = await axios.get(
    `${endPoints.company.zohoSettings(companyId)}`,
  );
  return response.data;
};

export const updateZohoSettingsApi = async (
  companyId: string,
  params: UpdateZohoSettingsParams,
): Promise<UpdateZohoSettingsResponse> => {
  const response = await axios.put(
    `${endPoints.company.zohoSettings(companyId)}`,
    params,
  );
  return response.data;
};

export const connectZohoApi = async (
  companyId: string,
): Promise<ConnectZohoResponse> => {
  const response = await axios.get(
    `${endPoints.company.connectZoho(companyId)}`,
  );
  return response.data;
};

export const getZohoTemplatesApi = async (
  companyId: string,
): Promise<{ templates: any[] }> => {
  const response = await axios.get(
    `${endPoints.company.zohoTemplates(companyId)}`,
  );
  return response.data;
};

export const getZohoTemplateDetailsApi = async (
  companyId: string,
  templateId: string,
): Promise<{ templates: any }> => {
  const response = await axios.get(
    `${endPoints.company.zohoTemplateDetails(companyId, templateId)}`,
  );
  return response.data;
};

export const updateSafeRecSettingsApi = async (
  companyId: string,
  params: UpdateSafeRecSettingsParams,
): Promise<UpdateSafeRecSettingsResponse> => {
  const response = await axios.put(
    `${endPoints.company.safeRecSettings(companyId)}`,
    params,
  );
  return response.data;
};

export const updateBoostworksSettingsApi = async (
  companyId: string,
  params: UpdateBoostworksSettingsParams,
): Promise<UpdateBoostworksSettingsResponse> => {
  const response = await axios.post(
    `${endPoints.company.boostworksSettings(companyId)}`,
    params,
  );
  return response.data;
};

export const updateVeriPayeSettingsApi = async (
  companyId: string,
  params: UpdateVeriPayeSettingsParams,
): Promise<UpdateVeriPayeSettingsResponse> => {
  const response = await axios.post(
    `${endPoints.company.veriPayeSettings(companyId)}`,
    params,
  );
  return response.data;
};

export const sendTestEmailApi = async (
  id: string,
  params: SendTestEmailParams,
): Promise<SendTestEmailResponse> => {
  const response = await axios.put(
    `${endPoints.company.testEmail}/${id}`,
    params,
  );
  return response.data;
};

export const sendTestEmailV2Api = async (
  params: SendTestEmailParams,
): Promise<SendTestEmailResponse> => {
  const response = await axios.put(
    `${endPoints.company.testEmailV2}`,
    params,
  );
  return response.data;
};

export const getEmailTemplateByIdApi = async (
  id: string,
): Promise<GetEmailTemplateByIdResponse> => {
  const response = await axios.get(
    `${endPoints.company.updateEmailTemplate}/${id}`,
  );
  return response.data;
};

export const updateEmailTemplateApi = async (
  id: string,
  params: UpdateEmailTemplateParams,
): Promise<UpdateEmailTemplateResponse> => {
  const response = await axios.put(
    `${endPoints.company.updateEmailTemplate}/${id}`,
    params,
  );
  return response.data;
};

export const getClientApplicationApi = async (
  params: GetClientApplicationParams,
): Promise<GetClientApplicationResponse> => {
  const response = await axios.get(`${endPoints.clientApplication.main}`, {
    params,
  });
  return response.data;
};

export const createClientApplicationApi = async (
  params: CreateClientApplicationParams,
): Promise<CreateClientApplicationResponse> => {
  const response = await axios.post(
    `${endPoints.clientApplication.main}/create`,
    params,
  );
  return response.data;
};

export const updateClientApplicationApi = async (
  id: number,
  params: UpdateClientApplicationParams,
): Promise<UpdateClientApplicationResponse> => {
  const response = await axios.put(
    `${endPoints.clientApplication.main}/${id}`,
    params,
  );
  return response.data;
};

export const createBannerApi = async (
  params: CreateBannerParams,
): Promise<CreateBannerResponse> => {
  const response = await axios.post(`${endPoints.banner.main}`, params);
  return response.data;
};

export const updateBannerApi = async (
  id: number,
  params: UpdateBannerParams,
): Promise<UpdateBannerResponse> => {
  const response = await axios.put(`${endPoints.banner.main}/${id}`, params);
  return response.data;
};

export const deleteBannerApi = async (
  id: number,
): Promise<DeleteBannerResponse> => {
  const response = await axios.delete(`${endPoints.banner.main}/${id}`);
  return response.data;
};

export const createWorkingPatternApi = async (
  params: CreateWorkingPatternParams,
): Promise<CreateWorkingPatternResponse> => {
  const response = await axios.post(
    `${endPoints.company.workingPattern}`,
    params,
  );
  return response.data;
};

export const updateWorkingPatternApi = async (
  id: number,
  params: UpdateWorkingPatternParams,
): Promise<UpdateWorkingPatternResponse> => {
  const response = await axios.put(
    `${endPoints.company.workingPattern}/${id}`,
    params,
  );
  return response.data;
};

export const deleteWorkingPatternApi = async (
  id: number,
): Promise<DeleteWorkingPatternResponse> => {
  const response = await axios.delete(
    `${endPoints.company.workingPattern}/${id}`,
  );
  return response.data;
};

export const checkSubDepartmentAssignApi = async (
  params: CheckSubDepartmentAssignParams,
): Promise<CheckSubDepartmentAssignResponse> => {
  // `detail` asks for employees and group names apart, not one total.
  const response = await axios.get(endPoints.company.isAssignedDepartment, {
    params: { ...params, detail: true },
  });
  return response.data;
};

export const getPrivateIrelandPensionApi = async (
  companyId: string,
): Promise<GetPrivateIrelandPensionResponse> => {
  const response = await axios.get(
    endPoints.company.privateIrelandPension(companyId),
  );
  return response.data;
};

export const createPrivateIrelandPensionApi = async (
  companyId: string,
  params: CreatePrivateIrelandPensionParams,
): Promise<CreatePrivateIrelandPensionResponse> => {
  const response = await axios.post(
    endPoints.company.privateIrelandPension(companyId),
    params,
  );
  return response.data;
};

export const updatePrivateIrelandPensionApi = async (
  companyId: string,
  pensionId: number,
  params: UpdatePrivateIrelandPensionParams,
): Promise<UpdatePrivateIrelandPensionResponse> => {
  const response = await axios.put(
    `${endPoints.company.privateIrelandPension(companyId)}/${pensionId}`,
    params,
  );
  return response.data;
};

export const deletePrivateIrelandPensionApi = async (
  companyId: string,
  pensionId: number,
): Promise<DeletePrivateIrelandPensionResponse> => {
  const response = await axios.delete(
    `${endPoints.company.privateIrelandPension(companyId)}/${pensionId}`,
  );
  return response.data;
};

export const toggleDefaultPrivateIrelandPensionApi = async (
  companyId: string,
  settingId: number,
): Promise<IPrivateIrelandPension> => {
  const response = await axios.post(
    endPoints.company.toggleDefaultPrivateIrelandPension(companyId, settingId),
  );
  return response.data;
};

export const updateCompanyStatusApi = async (
  companyId: string,
  params: { isActive: boolean; id: string },
): Promise<{ success: boolean; message: string }> => {
  const response = await axios.put(
    `${endPoints.company.updateCompany(companyId)}`,
    params,
  );
  return response.data;
};

export const createCompanyApi = async (
  params: CreateCompanyParams,
): Promise<CreateCompanyResponse> => {
  const response = await axios.post(`${endPoints.company.createV2}`, params);
  return response.data;
};

export const createCompanyDraftApi = async (
  params: CreateCompanyDraftParams,
): Promise<CreateCompanyDraftResponse> => {
  const response = await axios.post(`${endPoints.company.createDraft}`, params);
  return response.data;
};

export const connectNestPensionDraftApi = async (
  companyId: number,
  params: ConnectNestPensionParams,
): Promise<ConnectNestPensionResponse> => {
  const response = await axios.post(
    `${endPoints.company.connectNestPensionDraft(companyId)}`,
    params,
  );
  return response.data;
};

export const connectSmartPensionDraftApi = async (
  companyId: number,
  params: ConnectSmartPensionParams,
): Promise<ConnectSmartPensionDraftResponse> => {
  const response = await axios.post(
    `${endPoints.company.connectSmartPensionDraft(companyId)}`,
    {
      params,
    },
  );
  return response.data;
};

export const getCompanyDraftPensionStatusApi = async (
  draftId: number,
): Promise<GetCompanyDraftPensionStatusResponse> => {
  const response = await axios.get(
    `${endPoints.company.pensionStatusDraft(draftId)}`,
  );
  return response.data;
};

export const sendCompanyDraftLinkApi = async (
  draftId: number,
  params: SendCompanyDraftLinkParams,
): Promise<SendCompanyDraftLinkResponse> => {
  const response = await axios.post(
    `${endPoints.company.sendLinkDraft(draftId)}`,
    params,
  );
  return response.data;
};

// Company-level margin band table (company default margin method is a table).
// Deleting a row uses the shared `/timesheet-values/:id` endpoint.
export const getCompanyTimesheetValueByIdApi = async (
  id: string,
  params: GetAgencyTimesheetValueByIdParams,
): Promise<GetAgencyTimesheetValueByIdResponse> => {
  const response = await axios.get(
    `${endPoints.company.timesheetValue}/${id}`,
    { params },
  );
  return response.data;
};

export const createCompanyTimesheetValueByIdApi = async (
  id: string,
  params: CreateAgencyTimesheetValueByIdParams,
): Promise<CreateAgencyTimesheetValueByIdResponse> => {
  const response = await axios.post(
    `${endPoints.company.timesheetValue}/${id}`,
    params,
  );
  return response.data;
};